import axios from 'axios';
import { getEnvironmentConfig, getApiPrefix } from '../config/apiConfig';
import { getValidAdminToken, adminLogout, handleIpError } from '../auth/authManager';
import { getSelectedEndpoint } from '../endpoints/endpointManager';
import { getSelectedEndpointUrl, getAllEndpoints, storeEndpoints } from '../endpoints/endpointStore';

// Helper to get admin API key safely
const getAdminApiKey = () => {
  const config = getEnvironmentConfig();
  return config?.apiConfig?.ADMIN_API_KEY || '';
};

// Helper to get auth service endpoint safely
const getAuthServiceEndpoint = () => {
  const config = getEnvironmentConfig();
  return config?.authServiceEndpoint || '';
};

// Helper to get config service base URL from cache (no API call)
const getConfigServiceUrlFromCache = () => {
  const endpoint = getSelectedEndpointUrl();
  if (endpoint) {
    console.log('[baseAPI] Using cached config service URL:', endpoint);
    return endpoint;
  }
  return null;
};

// Helper to fetch and cache endpoints from auth service (only when needed)
const fetchAndCacheEndpoints = async () => {
  try {
    const response = await authServiceRequest('/endpoints');
    console.log('[baseAPI] Endpoints response:', response);
    
    // Parse the response data if it's a JSON string
    let endpoints = response.data;
    if (typeof endpoints === 'string') {
      endpoints = JSON.parse(endpoints);
      console.log('[baseAPI] Parsed endpoints:', endpoints);
    }
    
    if (Array.isArray(endpoints) && endpoints.length > 0) {
      // Store all endpoints in cache
      storeEndpoints(endpoints);
      console.log('[baseAPI] Endpoints cached:', endpoints.length, 'endpoints');
      
      // Get the first endpoint's baseUrl as the config service
      const firstEndpoint = endpoints[0];
      console.log('[baseAPI] Using config service from first DNS entry:', firstEndpoint.baseUrl);
      return firstEndpoint.baseUrl;
    }
    
    throw new Error('No endpoints available from auth service');
  } catch (error) {
    console.error('[baseAPI] Failed to get config service URL from auth:', error);
    throw error;
  }
};

// Create axios instance with base configuration
export const createApiInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = getValidAdminToken();
      
      // Add JWT Authorization header for IP-based auth
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        // Add x-api-key header (consistent with auth service)
        config.headers['x-api-key'] = getAdminApiKey();
        
        console.log('Request headers for discovered endpoint:', {
          url: config.url,
          Authorization: `Bearer ${token.substring(0, 20)}...`,
          'x-api-key': getAdminApiKey() ? 'present' : 'missing'
        });
      } else {
        // Check if we had a token in localStorage but it's now invalid (expired)
        const rawToken = localStorage.getItem('admin-token');
        if (rawToken) {
          console.warn('⚠️ TOKEN EXPIRED OR INVALID - TRIGGERING LOGOUT:', {
            url: config.url,
            method: config.method,
            rawTokenExists: !!rawToken,
            validTokenExists: false
          });
          
          // Token exists but is invalid/expired, trigger logout
          adminLogout();
          window.dispatchEvent(new Event('token-expired'));
          return Promise.reject(new Error('Authentication expired. Please log in again.'));
        } else {
          console.warn('⚠️ NO TOKEN AVAILABLE FOR REQUEST:', {
            url: config.url,
            method: config.method,
            headers: config.headers,
            localStorage_token: 'missing',
            localStorage_session: localStorage.getItem('admin-session') ? 'exists' : 'missing'
          });
        }
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('Unauthorized request:', {
          url: error.config?.url,
          headers: error.config?.headers,
          token: localStorage.getItem('admin-token') ? 'present' : 'missing',
          responseData: error.response?.data
        });
        
        // Check if this is a CORS preflight issue rather than auth issue
        if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
          console.warn('401 error appears to be CORS-related, not auth-related');
          return Promise.reject(error);
        }
        
        // Be more aggressive about logout on 401 errors
        // If we get 401 and we have a token, it's likely expired/invalid
        const rawToken = localStorage.getItem('admin-token');
        if (rawToken) {
          console.log('401 error with token present - likely expired, logging out');
          adminLogout();
          window.dispatchEvent(new Event('token-expired'));
        } else {
          console.log('401 error with no token - user needs to authenticate');
        }
      } else if (error.response?.status === 403) {
        const errorData = error.response.data;
        const isIpError = errorData?.code === 'IP_NOT_AUTHORIZED' ||
                          errorData?.error === 'Access denied - IP address not authorized' ||
                          (errorData?.details && errorData.details.includes('IP address'));
        
        if (isIpError) {
          console.error('IP not authorized:', errorData);
          handleIpError(errorData, error.response);
        } else {
          // Check if this is a permission/sub-role error
          const isPermissionError = errorData?.error === 'Insufficient sub-role permissions' ||
                                   (errorData?.category && errorData?.permission);
          
          if (isPermissionError) {
            console.error('Permission denied:', errorData);
            
            // Dispatch a custom event for permission errors that components can listen to
            const permissionDeniedEvent = new CustomEvent('permission-denied', {
              detail: {
                category: errorData.category || 'general',
                permission: errorData.permission || 'unknown',
                message: errorData.details || errorData.error || 'You do not have permission to perform this action',
                rawData: errorData
              }
            });
            window.dispatchEvent(permissionDeniedEvent);
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Function to get the current API instance (async)
export const getCurrentApi = async () => {
  try {
    // First try to get config service URL from cache (no API call)
    let configEndpoint = getConfigServiceUrlFromCache();
    
    // If no cached endpoint, fetch from auth service
    if (!configEndpoint) {
      console.log('[baseAPI] No cached endpoint, fetching from auth service...');
      configEndpoint = await fetchAndCacheEndpoints();
    }
    
    if (!configEndpoint) {
      console.warn('No endpoint available, cannot create API instance');
      return {
        get: () => Promise.reject(new Error('No endpoints available for API operations')),
        post: () => Promise.reject(new Error('No endpoints available for API operations')),
        put: () => Promise.reject(new Error('No endpoints available for API operations')),
        delete: () => Promise.reject(new Error('No endpoints available for API operations'))
      };
    }
    
    const apiPrefix = getApiPrefix();
    
    // Ensure endpoint has protocol prefix
    let normalizedEndpoint = configEndpoint;
    if (!normalizedEndpoint.startsWith('http://') && !normalizedEndpoint.startsWith('https://')) {
      normalizedEndpoint = `https://${normalizedEndpoint}`;
    }
    
    const baseURL = `${normalizedEndpoint}${apiPrefix}`;
    
    console.log('Creating API instance for endpoint:', {
      originalEndpoint: configEndpoint,
      normalizedEndpoint,
      apiPrefix: apiPrefix,
      baseURL: baseURL
    });
    
    return createApiInstance(baseURL);
  } catch (error) {
    console.error('Failed to get current API instance:', error);
    return {
      get: () => Promise.reject(new Error('Failed to create API instance: ' + error.message)),
      post: () => Promise.reject(new Error('Failed to create API instance: ' + error.message)),
      put: () => Promise.reject(new Error('Failed to create API instance: ' + error.message)),
      delete: () => Promise.reject(new Error('Failed to create API instance: ' + error.message))
    };
  }
};

// Utility function for authenticated admin requests
export const adminRequest = async (url, options = {}) => {
  const token = getValidAdminToken();
  
  if (!token) {
    // Check if we had a token that's now expired
    const rawToken = localStorage.getItem('admin-token');
    if (rawToken) {
      console.log('Token expired during admin request, logging out');
      adminLogout();
      window.dispatchEvent(new Event('token-expired'));
    }
    throw new Error('No admin token found. Please authenticate first.');
  }
  
  // Check if we have a valid endpoint
  const currentEndpoint = getSelectedEndpoint();
  if (!currentEndpoint) {
    throw new Error('No endpoints available for admin operations. Please check endpoint configuration.');
  }

  try {
    // For admin endpoints, use the current endpoint with the configured API prefix
    const apiPrefix = getApiPrefix();
    const adminUrl = url.startsWith('/') ? url : `/${url}`;
    const fullUrl = `${currentEndpoint}${apiPrefix}/admin${adminUrl}`;
    
    console.log('Admin request:', {
      url: adminUrl,
      fullUrl,
      method: options.method || 'GET',
      endpoint: currentEndpoint,
      apiPrefix: apiPrefix,
      token: token ? `${token.substring(0, 20)}...` : 'missing',
      adminApiKey: getAdminApiKey() ? 'present' : 'missing'
    });
    
    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getAdminApiKey(),
        ...options.headers
      },
      timeout: 10000,
      ...options
    });
    
    return response;
  } catch (error) {
    // Handle specific error types
    const adminErrorData = error.response?.data;
    const adminIsIpError = adminErrorData?.code === 'IP_NOT_AUTHORIZED' ||
                           adminErrorData?.error === 'Access denied - IP address not authorized' ||
                           (adminErrorData?.details && adminErrorData.details.includes('IP address'));
    
    if (error.response?.status === 403 && adminIsIpError) {
      handleIpError(error.response.data, error.response);
      throw new Error('Access denied: IP not authorized');
    }
    
    // Handle CORS errors separately from auth errors
    if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
      const apiPrefix = getApiPrefix();
      console.error('CORS error in admin request:', {
        url,
        fullUrl: currentEndpoint ? `${currentEndpoint}${apiPrefix}/admin${url}` : 'No endpoint available',
        message: 'CORS policy blocking request',
        suggestion: 'Server needs CORS headers for localhost:3000'
      });
      throw new Error('CORS policy blocks this request. Server needs CORS configuration.');
    }
    
    // Handle 401 errors - be more aggressive about logout
    if (error.response?.status === 401) {
      const apiPrefix = getApiPrefix();
      console.error('401 Unauthorized in admin request:', {
        url,
        fullUrl: currentEndpoint ? `${currentEndpoint}${apiPrefix}/admin${url}` : 'No endpoint available',
        responseData: error.response?.data,
        headers: error.config?.headers,
        token: token ? 'present' : 'missing'
      });
      
      // Don't automatically logout for CORS-related 401s
      if (!error.message.includes('CORS')) {
        console.log('401 error in admin request - token likely expired, logging out');
        adminLogout();
        window.dispatchEvent(new Event('token-expired'));
        throw new Error('Authentication expired. Please log in again.');
      }
    }
    
    console.error('Admin request failed:', {
      url,
      fullUrl: currentEndpoint ? `${currentEndpoint}${getApiPrefix()}/admin${url}` : 'No endpoint available',
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      headers: error.config?.headers,
      token: token ? 'present' : 'missing'
    });
    throw error;
  }
};

// Utility function for direct auth service requests (bypasses discovered endpoints)
export const authServiceRequest = async (url, options = {}) => {
  const token = getValidAdminToken();
  
  if (!token) {
    throw new Error('No admin token found. Please authenticate first.');
  }
  
  try {
    // For auth service endpoints, use the hardcoded auth service URL directly
    const authUrl = url.startsWith('/') ? url : `/${url}`;
    const fullUrl = `${getAuthServiceEndpoint()}/api/admin${authUrl}`;
    
    console.log('Auth service request:', {
      url: authUrl,
      fullUrl,
      method: options.method || 'GET',
      token: token ? `${token.substring(0, 20)}...` : 'missing',
      apiKey: getAdminApiKey() ? 'present' : 'missing'
    });
    
    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getAdminApiKey(),
        ...options.headers
      },
      timeout: 10000,
      ...options
    });
    
    return response;
  } catch (error) {
    // Handle IP validation errors
    const authErrorData = error.response?.data;
    const authIsIpError = authErrorData?.code === 'IP_NOT_AUTHORIZED' ||
                          authErrorData?.error === 'Access denied - IP address not authorized' ||
                          (authErrorData?.details && authErrorData.details.includes('IP address'));
    
    if (error.response?.status === 403 && authIsIpError) {
      handleIpError(error.response.data, error.response);
      throw new Error('Access denied: IP not authorized');
    }
    console.error('Auth service request failed:', {
      url,
      fullUrl: `${getAuthServiceEndpoint()}/api/admin${url}`,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      headers: error.config?.headers,
      token: token ? 'present' : 'missing'
    });
    throw error;
  }
};

export default getCurrentApi;

// Create a service-specific API instance that bypasses the /admin prefix
export const createServiceApiInstance = async () => {
  try {
    // First try to get config service URL from cache (no API call)
    let configEndpoint = getConfigServiceUrlFromCache();
    
    // If no cached endpoint, fetch from auth service
    if (!configEndpoint) {
      console.log('[baseAPI] No cached endpoint, fetching from auth service...');
      configEndpoint = await fetchAndCacheEndpoints();
    }
    
    if (!configEndpoint) {
      console.warn('No endpoint available, cannot create API instance');
      return null;
    }
    
    const apiPrefix = getApiPrefix();
    
    // Ensure endpoint has protocol prefix
    let normalizedEndpoint = configEndpoint;
    if (!normalizedEndpoint.startsWith('http://') && !normalizedEndpoint.startsWith('https://')) {
      normalizedEndpoint = `https://${normalizedEndpoint}`;
    }
    
    // For service endpoints, we use the base URL without /admin prefix
    const baseURL = `${normalizedEndpoint}${apiPrefix}`;
    
    console.log('Creating service API instance for endpoint:', {
      originalEndpoint: configEndpoint,
      normalizedEndpoint,
      apiPrefix: apiPrefix,
      baseURL: baseURL
    });
    
    return createApiInstance(baseURL);
  } catch (error) {
    console.error('Failed to create service API instance:', error);
    return null;
  }
};

// Utility function for service-specific requests (bypasses /admin prefix)
export const serviceRequest = async (url, options = {}) => {
  const token = getValidAdminToken();
  
  if (!token) {
    throw new Error('No admin token found. Please authenticate first.');
  }
  
  // Check if we have a valid endpoint
  const currentEndpoint = getSelectedEndpoint();
  if (!currentEndpoint) {
    throw new Error('No endpoints available for service operations. Please check endpoint configuration.');
  }

  try {
    // For service endpoints, we use the current endpoint directly without /admin prefix
    const apiPrefix = getApiPrefix();
    const serviceUrl = url.startsWith('/') ? url : `/${url}`;
    const fullUrl = `${currentEndpoint}${apiPrefix}${serviceUrl}`;
    
    console.log('Service request:', {
      url: serviceUrl,
      fullUrl,
      method: options.method || 'GET',
      endpoint: currentEndpoint,
      apiPrefix: apiPrefix,
      token: token ? `${token.substring(0, 20)}...` : 'missing',
      adminApiKey: getAdminApiKey() ? 'present' : 'missing'
    });
    
    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getAdminApiKey(),
        ...options.headers
      },
      timeout: 10000,
      ...options
    });
    
    return response;
  } catch (error) {
    // Handle specific error types
    const serviceErrorData = error.response?.data;
    const serviceIsIpError = serviceErrorData?.code === 'IP_NOT_AUTHORIZED' ||
                            serviceErrorData?.error === 'Access denied - IP address not authorized' ||
                            (serviceErrorData?.details && serviceErrorData.details.includes('IP address'));
    
    if (error.response?.status === 403 && serviceIsIpError) {
      handleIpError(error.response.data, error.response);
      throw new Error('Access denied: IP not authorized');
    }
    
    console.error('Service request failed:', {
      url,
      fullUrl: currentEndpoint ? `${currentEndpoint}${getApiPrefix()}${url}` : 'No endpoint available',
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      headers: error.config?.headers,
      token: token ? 'present' : 'missing'
    });
    throw error;
  }
};